/**
 * PropertyInfo — the main (left) column of a property card: gallery, price,
 * title, description, beds/baths, disclaimer and features.
 *
 * The size stats, build year and fees now live in the right-column Property
 * Details card (see PropertyDetails.js); the location map and buying costs are
 * rendered in the card's "tail" (see PropertySection / LocationMap).
 *
 * Most values come straight from the offer view-model (see mapProperty.js).
 *   - `countRange` formats beds/baths that may carry a `*To` upper bound
 *
 * Props:
 *   - offer            {Object}    — the offer view-model
 *   - onViewFacilities {()=>void}  — open the "All features" modal
 */
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useStrings, useLocalization, propertyTitle, propertyTitleLines, featureLabel } from './i18n';
import { useIsMobile } from './hooks';
import { IconChevronDown, IconChevronRight, IconBed, IconBath, IconCheck, IconExpand } from './icons';
import Gallery from './Gallery';
import Money from './Money';
import StatusRibbon from './StatusRibbon';

export default function PropertyInfo({ offer, onViewFacilities, detailsExpanded, onToggleExpand, showFeatures = true, compact = false, navArrow = false, hideToggle = false }) {
    const t = useStrings();
    const l = useLocalization();
    const isMobile = useIsMobile();
    const [expanded, setExpanded] = useState(false);
    const [showAllFeatures, setShowAllFeatures] = useState(false); // desktop: expand features inline
    const [clamped, setClamped] = useState(false); // true only when the text actually overflows the clamp
    const descRef = useRef(null);

    // Features grouped by API category, in mapProperty's order (priority
    // categories — pool / views / parking / Setting — first, then largest-first),
    // with a single "Features" fallback when the feed has no categories.
    const featureCats = offer.featureCategories && offer.featureCategories.length
        ? offer.featureCategories
        : [{ category: t.features, items: offer.facilities.length ? offer.facilities : [t.infoOnRequest] }];

    // First 8 features across the (prioritised) categories for the inline preview,
    // keeping each item's category so bare qualifiers can be clarified.
    const previewFeatures = [];
    for (let ci = 0; ci < featureCats.length && previewFeatures.length < 8; ci += 1) {
        const c = featureCats[ci];
        for (let ii = 0; ii < c.items.length && previewFeatures.length < 8; ii += 1) {
            previewFeatures.push({ category: c.category, item: c.items[ii] });
        }
    }

    // Only show "More" when the clamped description is genuinely truncated.
    // Measured while clamped (scrollHeight > clientHeight); re-checked on resize
    // and once webfonts settle (the clamp height shifts with the real font).
    useEffect(() => {
        const el = descRef.current;
        if (!el) return undefined;
        let cancelled = false;
        const measure = () => {
            if (cancelled || expanded) return; // only meaningful while clamped
            setClamped(el.scrollHeight - el.clientHeight > 1);
        };
        measure();
        window.addEventListener('resize', measure);
        if (document.fonts && document.fonts.ready) document.fonts.ready.then(measure);
        return () => { cancelled = true; window.removeEventListener('resize', measure); };
        // detailsExpanded: on mobile the description sits in a collapsed section
        // (display:none) until the card expands — re-measure once it's visible,
        // else scrollHeight/clientHeight are 0 and the "More" toggle never shows.
    }, [expanded, detailsExpanded, offer.description]);

    // Beds/baths count: "from - to" when a range is present, else just "from".
    const countRange = (from, to) => (to > from ? `${from} - ${to}` : `${from}`);

    // Rentals show a payment period next to the price (long-term = /month,
    // short-term = /week); sales show nothing.
    const pricePeriod = offer.isRental ? (offer.isShortTerm ? t.perWeek : t.perMonth) : '';

    // Beds/baths pills. Sit on the price row on both desktop and mobile.
    const bedbath = !offer.isUnavailable && (
        <div className="rs-bb">
            <div className="rs-bb__item">
                <span className="rs-bb__icon"><IconBed size={25} /></span>
                <span className="rs-bb__text"><b>{countRange(offer.beds, offer.bedsTo)}</b></span>
            </div>
            <div className="rs-bb__item">
                <span className="rs-bb__icon"><IconBath size={25} /></span>
                <span className="rs-bb__text"><b>{countRange(offer.baths, offer.bathsTo)}</b></span>
            </div>
        </div>
    );

    // Floor-space for the compact pill (built area, falling back to useful area).
    const sizeVal = offer.stats.build.from || offer.stats.intFloor.from;
    const sizeLabel = sizeVal ? `${sizeVal} ${offer.unit}` : null;

    // Mobile compact overlay: if beds/baths/size don't fit on one line, drop the
    // size pill (keep beds + baths) rather than wrapping to a second line. We
    // compare the three pills' intrinsic widths to the container — the size pill
    // stays measurable even when hidden (it goes position:absolute, not display:
    // none), so the decision can't flip-flop.
    const pillsRef = useRef(null);
    const [hideSize, setHideSize] = useState(false);
    useLayoutEffect(() => {
        if (!isMobile || !sizeLabel) { setHideSize(false); return undefined; }
        const el = pillsRef.current;
        if (!el) return undefined;
        const measure = () => {
            const box = el.clientWidth;
            if (!box) return; // not laid out yet (e.g. hidden)
            const kids = el.querySelectorAll('.rs-compact__pill');
            if (kids.length < 3) return;
            const cs = window.getComputedStyle(el);
            const gap = parseFloat(cs.columnGap || cs.gap || '0') || 0;
            let sum = 0;
            kids.forEach((k) => { sum += k.getBoundingClientRect().width; });
            sum += gap * (kids.length - 1);
            setHideSize(sum > box + 0.5);
        };
        measure();
        let ro;
        if (typeof ResizeObserver !== 'undefined') { ro = new ResizeObserver(measure); ro.observe(el); }
        window.addEventListener('resize', measure);
        return () => { if (ro) ro.disconnect(); window.removeEventListener('resize', measure); };
    }, [isMobile, sizeLabel, offer.beds, offer.bedsTo, offer.baths, offer.bathsTo]);

    // Collapsed (mobile): a frosted GLASS inset over the photo — title, price and
    // spec pills — keeping the collapsed card image-led. Type/location already
    // live in the title, so they aren't repeated. Both this and the expanded
    // white title card stay MOUNTED on mobile and cross-fade by card state (see
    // .rs-property-card--collapsed in the SCSS), so toggling doesn't pop. Desktop: null.
    const compactOverlay = isMobile ? (
        <div className="rs-compact">
            <div className="rs-compact__panel">
                <div className="rs-compact__title">{propertyTitle(l, offer)}</div>
                {/* price + spec pills collapse while paging photos (Gallery adds
                    .is-nav), and slide back after 5s of no navigation */}
                <div className="rs-compact__specs">
                    {offer.isUnavailable
                        ? <div className="rs-compact__price rs-unavailable">{t.unitUnavailable}</div>
                        : <div className="rs-compact__price"><Money>{offer.priceLabel}</Money>{pricePeriod && <span className="rs-price__period">{pricePeriod}</span>}</div>}
                    {!offer.isUnavailable && <div className="rs-compact__pills" ref={pillsRef}>
                        <span className="rs-compact__pill"><IconBed size={18} /> {countRange(offer.beds, offer.bedsTo)}</span>
                        <span className="rs-compact__pill"><IconBath size={18} /> {countRange(offer.baths, offer.bathsTo)}</span>
                        {sizeLabel ? (
                            <span className={`rs-compact__pill rs-compact__pill--size ${hideSize ? 'is-hidden' : ''}`}>
                                <IconExpand size={16} /> {sizeLabel}
                            </span>
                        ) : null}
                    </div>}
                </div>
            </div>
        </div>
    ) : null;

    // Expanded (mobile): only the title sits on the photo, on a WHITE card (not
    // glass). Price and beds/baths return to their normal spot below the image
    // (the rs-priceline), matching the original uncollapsed layout.
    const titleOverlay = isMobile ? (
        <div className="rs-titlecard">
            <div className="rs-titlecard__panel">{propertyTitle(l, offer)}</div>
        </div>
    ) : null;

    // Desktop/tablet: the title sits INSIDE the carousel — a glass panel on the
    // right half of the image, vertically centered (mobile keeps its own compact
    // / title overlays).
    const [leadType, leadAction, leadLocation] = propertyTitleLines(l, offer);
    const leadOverlay = !isMobile ? (
        <div className="rs-lead">
            <h1 className="rs-lead__panel">
                <span className="rs-lead__type">{leadType}</span>
                {leadAction ? <span className="rs-lead__action">{leadAction}</span> : null}
                {leadLocation ? <span className="rs-lead__loc">{leadLocation}</span> : null}
            </h1>
        </div>
    ) : null;

    // Corner ribbon for in-progress sale statuses — shared with the grid cards
    // (see StatusRibbon). Rendered on every surface the gallery serves (desktop
    // band, mobile card, collapsed mobile card).
    const statusRibbon = <StatusRibbon statusBanner={offer.statusBanner} l={l} unavailable={offer.isUnavailable} />;

    // Shared gallery element. On desktop/tablet it lives in the full-width media
    // band (spanning both grid columns); on mobile it stays in the info flow so
    // its card-edge bleed and spacing are unchanged. Only one branch renders.
    // Widths are no longer picked here: the Gallery emits a srcset/sizes so the
    // browser chooses by viewport and pixel ratio (a 1x phone takes w400 rather
    // than the w800 this used to force, and the lightbox is no longer capped).
    const gallery = (
        <Gallery
            images={offer.gallery}
            photoCount={offer.photoCount}
            overlay={<>{compactOverlay}{titleOverlay}{leadOverlay}{statusRibbon}</>}
        />
    );

    return (
        <>
        {/* Desktop/tablet: carousel as a full-width band across the info + sidebar
            columns; the title overlays the image (see leadOverlay). */}
        {!isMobile && <div className={`rs-grid__media${compact ? ' rs-grid__media--contained' : ''}`}>{gallery}</div>}
        <div className="rs-grid__info">
            {isMobile && gallery}

            <div className="rs-priceline">
                {offer.isUnavailable
                    ? <div className="rs-price rs-unavailable">{t.unitUnavailable}</div>
                    : <div className="rs-price"><Money>{offer.priceLabel}</Money>{pricePeriod && <span className="rs-price__period">{pricePeriod}</span>}</div>}
                {bedbath}
            </div>
            {isMobile && <h1 className="rs-title">{propertyTitle(l, offer)}</h1>}

            {/* Mobile-only: reveal/hide the rest of the card (features, details,
                certificates, description, map, buying costs). Hidden on desktop —
                and on the grid detail page (hideToggle), which is always expanded.
                In grid results mode (navArrow) the button OPENS the detail page,
                so it points right instead of down. */}
            {isMobile && !hideToggle && (
                <button
                    className="rs-pill rs-pill--tint rs-expand"
                    aria-expanded={detailsExpanded}
                    onClick={onToggleExpand}
                >
                    {detailsExpanded ? t.hideDetails : t.viewDetails}
                    {navArrow ? <IconChevronRight size={18} /> : <IconChevronDown size={18} />}
                </button>
            )}

            <section className="rs-section">
                <div className="rs-descbox">
                    <h2 className="rs-section__h">{t.description}</h2>
                    <p ref={descRef} className={`rs-desc ${expanded ? '' : 'is-clamped'}`}>{offer.description}</p>
                    {(clamped || expanded) && (
                        <button className={`rs-more ${expanded ? 'is-open' : ''}`} onClick={() => setExpanded((v) => !v)}>
                            {expanded ? t.less : t.more} <IconChevronDown size={18} />
                        </button>
                    )}
                </div>
            </section>

            {offer.disclaimer ? (
                <section className="rs-section">
                    <h2 className="rs-section__h" style={{ fontSize: 14 }}>{t.disclaimer}</h2>
                    <p className="rs-disclaimer">{offer.disclaimer}</p>
                </section>
            ) : null}

            {showFeatures && (
            <section className="rs-section">
                <div className="rs-facils">
                    <h2 className="rs-section__h">{t.features}</h2>
                    {showAllFeatures ? (
                        <>
                            {/* Desktop: all features expanded inline, grouped by category. */}
                            <div className="rs-feat-cats">
                                {featureCats.map((cat) => (
                                    <div className="rs-feat-cat" key={cat.category}>
                                        <div className="rs-feat-cat__h">{cat.category}</div>
                                        <ul className="rs-feat-cat__list">
                                            {cat.items.map((f) => (
                                                <li className="rs-facil" key={f}>
                                                    <span className="rs-facil__check"><IconCheck size={12} /></span>{featureLabel(t, cat.category, f)}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                ))}
                            </div>
                            <button className="rs-pill rs-pill--tint rs-facils__more" onClick={() => setShowAllFeatures(false)}>
                                {t.seeLess}
                            </button>
                        </>
                    ) : (
                        <>
                            <div className="rs-facils__grid">
                                {previewFeatures.map(({ category, item }, i) => (
                                    <div className="rs-facil" key={`${category}-${item}-${i}`}>
                                        <span className="rs-facil__check"><IconCheck size={12} /></span>{featureLabel(t, category, item)}
                                    </div>
                                ))}
                            </div>
                            {offer.facilities.length > 8 && (
                                // Mobile keeps the dialog; desktop expands the container inline.
                                <button className="rs-pill rs-pill--tint rs-facils__more"
                                    onClick={() => (isMobile ? onViewFacilities() : setShowAllFeatures(true))}>
                                    {t.viewAllFeatures}
                                </button>
                            )}
                        </>
                    )}
                </div>
            </section>
            )}
        </div>
        </>
    );
}
